import React from 'react';
import { TextField, MenuItem, Typography, Box, Grid, Button, IconButton } from '@mui/material';
import { ControlDomicilio } from './ControlesConFormato';
import { formatearFechaMientrasEscribe } from '../../../utils/validacionesCaratula';

// Tinta del documento: el mismo gris azulado que usa el resto de las hojas imprimibles
// (ver components/ClienteImpresion.js).
export const COLOR_TINTA = '#1e293b';

export const OPCIONES_SI_NO = [
    { value: 'SI', label: 'SI' },
    { value: 'NO', label: 'NO' },
];

export const OPCIONES_BENEFICIO = [
    { value: 'JUBILACION', label: 'JUBILACION' },
    { value: 'PENSION', label: 'PENSION' },
];

export const OPCIONES_ESTADO_CIVIL = [
    { value: 'SOLTERO', label: 'Soltero/a' },
    { value: 'CASADO', label: 'Casado/a' },
    { value: 'DIVORCIADO', label: 'Divorciado/a' },
    { value: 'VIUDO', label: 'Viudo/a' },
    { value: 'SEPARADO', label: 'Separado/a' },
    { value: 'CONCUBINATO', label: 'Concubinato' },
];

/**
 * Nombre de la propiedad del DTO que edita un renglón. Casi siempre es la key del mapa
 * CAMPOS, salvo los renglones que existen en dos variantes de la misma carátula con
 * etiqueta distinta pero comparten campo (CUENTA EN AFIP / CUENTA AUTONOMO en Jubilación,
 * MONTO DIFERENCIA / Montos diferencias en Reajuste): esos declaran `campo`.
 */
export const claveDeCampo = (campos, campoKey) => campos?.[campoKey]?.campo || campoKey;

/**
 * Solo el input de un renglón, sin etiqueta. Lo usa CampoCaratula y también las celdas de las
 * tablas del papel (grilla de herederos, claves de Ganancias), donde la etiqueta la pone el
 * encabezado de la columna o la celda de al lado.
 *
 * Tipos con formato (validados en utils/validacionesCaratula.js, `error` trae el mensaje):
 *  - fecha: máscara DD/MM/AAAA (las barras se ponen solas, solo dígitos).
 *  - email: correo con @, dominio y extensión.
 *  - clave: texto visible (el operador la lee para cargarla en el papel); solo se limita el largo.
 *  - domicilio: Calle · Nro · Localidad del catálogo; necesita `form` para leer y escribir el
 *    objeto estructurado (`campo.estructurado`) además del String del renglón.
 */
export function ControlCaratula({ campo, clave, valor, onChange, sinLinea = false, error = null, form = null }) {
    const esMultilinea = campo.tipo === 'multilinea';

    const propsInput = {
        value: valor ?? '',
        onChange: (e) => onChange(clave, e.target.value),
        variant: 'standard',
        fullWidth: true,
        error: Boolean(error),
        helperText: error || undefined,
        placeholder: campo.placeholder,
        // El tooltip le dice al operador si lo que escribe queda guardado o es solo para
        // este documento; los renglones con `nota` explican su propio caso.
        title: campo.nota || (campo.guardable
            ? 'Se guarda en el cliente/trámite'
            : 'Solo se usa para este documento'),
        InputProps: {
            // Dentro de una celda con bordes la línea del input sobra: el borde ya la dibuja.
            ...(sinLinea ? { disableUnderline: true } : {}),
            sx: {
                fontFamily: 'inherit',
                fontSize: '0.9rem',
                color: COLOR_TINTA,
                '&:before': { borderBottomColor: '#cbd5e1' },
                '&:hover:not(.Mui-disabled):before': { borderBottomColor: '#3B82F6' },
            },
        },
    };

    if (campo.tipo === 'fecha') {
        return (
            <TextField
                {...propsInput}
                placeholder={campo.placeholder || 'DD/MM/AAAA'}
                onChange={(e) => onChange(clave, formatearFechaMientrasEscribe(e.target.value))}
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            />
        );
    }
    if (campo.tipo === 'email') {
        return (
            <TextField
                {...propsInput}
                type="email"
                placeholder={campo.placeholder || 'nombre@dominio.com'}
                inputProps={{ inputMode: 'email', maxLength: 100 }}
            />
        );
    }
    if (campo.tipo === 'clave') {
        return (
            <TextField
                {...propsInput}
                autoComplete="off"
                inputProps={{ 'aria-label': `Clave: ${campo.label}`, maxLength: 100 }}
            />
        );
    }
    if (campo.tipo === 'domicilio' && form) {
        return (
            <ControlDomicilio
                campo={campo}
                clave={clave}
                form={form}
                onChange={onChange}
                error={error}
                propsBase={propsInput}
            />
        );
    }
    if (campo.tipo === 'sino') {
        return (
            <TextField select {...propsInput}>
                {OPCIONES_SI_NO.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
        );
    }
    if (campo.tipo === 'beneficio') {
        return (
            <TextField select {...propsInput}>
                {OPCIONES_BENEFICIO.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
        );
    }
    if (campo.tipo === 'estadoCivil') {
        return (
            <TextField select {...propsInput}>
                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                {OPCIONES_ESTADO_CIVIL.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
        );
    }
    if (esMultilinea) {
        return <TextField {...propsInput} multiline rows={3} />;
    }
    return <TextField {...propsInput} />;
}

/**
 * Renglón del documento: etiqueta en negrita + valor sobre una línea, como en el papel.
 *
 * `campo` es el descriptor que sale del mapa CAMPOS de cada modal:
 *   { label, tipo, guardable?, resaltada?, nota?, campo?, placeholder?, noFutura?, estructurado? }
 * y `tipo` es uno de: texto | sino | estadoCivil | beneficio | multilinea | fecha | email |
 * clave | domicilio.
 */
export function CampoCaratula({ campo, clave, valor, onChange, error = null, form = null }) {
    const esMultilinea = campo.tipo === 'multilinea' || campo.tipo === 'domicilio';
    const control = (
        <ControlCaratula campo={campo} clave={clave} valor={valor} onChange={onChange} error={error} form={form} />
    );

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: esMultilinea ? 'flex-start' : 'baseline',
                flexDirection: esMultilinea ? 'column' : 'row',
                gap: esMultilinea ? 0.5 : 1,
                mb: 1,
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    color: COLOR_TINTA,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    // El estudio resalta REFERENCIA en el documento a propósito.
                    ...(campo.resaltada ? { backgroundColor: '#ff00ff', px: 0.5 } : {}),
                }}
            >
                {campo.label}:
            </Typography>
            <Box sx={{ flexGrow: 1, width: esMultilinea ? '100%' : 'auto', minWidth: 0 }}>
                {control}
            </Box>
        </Box>
    );
}

const BORDE_TABLA = '1px solid #64748b';

const estiloCelda = {
    border: BORDE_TABLA,
    px: 1,
    py: 0.25,
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    color: COLOR_TINTA,
    verticalAlign: 'middle',
};

/**
 * Arma los helpers de dibujo que usan todas las carátulas, atados a un mapa CAMPOS y al
 * formulario en curso. `renderFila` recibe una de estas formas:
 *
 *  - `[keys]` — una sola key ocupa el renglón entero, varias se reparten el ancho, igual que
 *    los renglones compuestos del papel.
 *  - `{ keys, apilado: true }` — varias keys una debajo de la otra, dentro del mismo bloque
 *    (causante + DNI en Declaratoria: en el papel no comparten línea).
 *  - `{ separador: true }` — la línea horizontal que separa los bloques en el papel.
 *  - `{ tabla: { encabezados?, filas, anchoMaximo? } }` — una tabla con bordes. Cada celda de
 *    `filas` es una key de CAMPOS (se dibuja solo el input) o `{ texto }` (celda fija).
 *  - `{ grilla: { clave, columnas: [{ key, encabezado }], filasMinimas?, etiquetaFila? } }` —
 *    tabla con CANTIDAD VARIABLE de filas: `form[clave]` es una lista de objetos, una por fila
 *    (herederos de Declaratoria). Siempre muestra al menos `filasMinimas` (las del papel) y
 *    permite agregar y quitar. Texto libre, solo para el documento: no pasa por la validación.
 *
 * `errores` es el mapa { campoKey: mensaje } de validarFormulario (utils/validacionesCaratula).
 */
export function crearRenderizadores(campos, form, onChange, errores = {}) {
    const renderCampo = (campoKey) => {
        const campo = campos[campoKey];
        if (!campo) return null;
        const clave = campo.campo || campoKey;
        return (
            <CampoCaratula
                campo={campo}
                clave={clave}
                valor={form[clave]}
                onChange={onChange}
                error={errores[campoKey]}
                form={form}
            />
        );
    };

    const renderControl = (campoKey) => {
        const campo = campos[campoKey];
        if (!campo) return null;
        const clave = campo.campo || campoKey;
        return (
            <ControlCaratula
                campo={campo}
                clave={clave}
                valor={form[clave]}
                onChange={onChange}
                error={errores[campoKey]}
                form={form}
                sinLinea
            />
        );
    };

    const renderTabla = ({ encabezados, filas, anchoMaximo }, idx) => (
        <Box
            key={idx}
            component="table"
            sx={{ width: '100%', maxWidth: anchoMaximo, borderCollapse: 'collapse', my: 1.5, tableLayout: 'fixed' }}
        >
            {encabezados && (
                <thead>
                    <tr>
                        {encabezados.map((e) => (
                            <Box component="th" key={e} sx={{ ...estiloCelda, fontWeight: 700, textAlign: 'center' }}>{e}</Box>
                        ))}
                    </tr>
                </thead>
            )}
            <tbody>
                {filas.map((celdas, i) => (
                    <tr key={i}>
                        {celdas.map((celda, j) => (
                            <Box component="td" key={j} sx={{ ...estiloCelda, fontWeight: celda?.texto ? 700 : 400 }}>
                                {typeof celda === 'string' ? renderControl(celda) : celda?.texto}
                            </Box>
                        ))}
                    </tr>
                ))}
            </tbody>
        </Box>
    );

    const renderGrilla = ({ clave, columnas, filasMinimas = 0, etiquetaFila = 'fila' }, idx) => {
        const cargadas = Array.isArray(form[clave]) ? form[clave] : [];
        const filaVacia = () => Object.fromEntries(columnas.map((c) => [c.key, '']));
        const filas = cargadas.length >= filasMinimas
            ? cargadas
            : [...cargadas, ...Array.from({ length: filasMinimas - cargadas.length }, filaVacia)];
        const cambiarCelda = (i, key, valor) =>
            onChange(clave, filas.map((fila, j) => (j === i ? { ...fila, [key]: valor } : fila)));
        const quitarFila = (i) => onChange(clave, filas.filter((_, j) => j !== i));
        const sePuedeQuitar = filas.length > filasMinimas;

        return (
            <Box key={idx} sx={{ my: 1.5 }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            {columnas.map((c) => (
                                <Box component="th" key={c.key} sx={{ ...estiloCelda, fontWeight: 700, textAlign: 'center' }}>
                                    {c.encabezado}
                                </Box>
                            ))}
                            {/* Columna del botón de quitar: fuera de los bordes, no es parte del papel. */}
                            <Box component="th" sx={{ width: 40 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {filas.map((fila, i) => (
                            <tr key={i}>
                                {columnas.map((c) => (
                                    <Box component="td" key={c.key} sx={estiloCelda}>
                                        <TextField
                                            value={fila?.[c.key] ?? ''}
                                            onChange={(e) => cambiarCelda(i, c.key, e.target.value)}
                                            variant="standard"
                                            fullWidth
                                            title="Solo se usa para este documento"
                                            inputProps={{ 'aria-label': `${c.encabezado} ${etiquetaFila} ${i + 1}`, maxLength: 150 }}
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: { fontFamily: 'inherit', fontSize: '0.9rem', color: COLOR_TINTA },
                                            }}
                                        />
                                    </Box>
                                ))}
                                <Box component="td" sx={{ textAlign: 'center' }}>
                                    {sePuedeQuitar && (
                                        <IconButton
                                            size="small"
                                            aria-label={`Quitar ${etiquetaFila} ${i + 1}`}
                                            onClick={() => quitarFila(i)}
                                        >
                                            <i className="bi bi-x-lg" style={{ fontSize: '0.8rem' }}></i>
                                        </IconButton>
                                    )}
                                </Box>
                            </tr>
                        ))}
                    </tbody>
                </Box>
                <Button
                    size="small"
                    sx={{ mt: 0.5, textTransform: 'none' }}
                    startIcon={<i className="bi bi-plus-lg"></i>}
                    onClick={() => onChange(clave, [...filas, filaVacia()])}
                >
                    Agregar {etiquetaFila}
                </Button>
            </Box>
        );
    };

    const renderFila = (fila, idx) => {
        if (fila?.separador) {
            return <Box key={idx} sx={{ borderTop: `1.5px solid ${COLOR_TINTA}`, my: 1 }} />;
        }
        if (fila?.tabla) {
            return renderTabla(fila.tabla, idx);
        }
        if (fila?.grilla) {
            return renderGrilla(fila.grilla, idx);
        }
        if (fila?.apilado) {
            return <Box key={idx}>{fila.keys.map((key) => <Box key={key}>{renderCampo(key)}</Box>)}</Box>;
        }
        const keys = fila;
        if (keys.length === 1) {
            return <Box key={idx}>{renderCampo(keys[0])}</Box>;
        }
        return (
            <Grid container spacing={2} key={idx}>
                {keys.map((key) => (
                    <Grid key={key} size={{ xs: 12, sm: 12 / keys.length }}>{renderCampo(key)}</Grid>
                ))}
            </Grid>
        );
    };

    return { renderCampo, renderControl, renderFila };
}
